import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "src/hooks/useApi";

interface TrialLimitsData {
    invoiceCount: number;
    invoiceLimit: number;
    smsCount: number;
    smsLimit: number;
    daysLeft: number;
}

interface TrialLimitBarProps {
    label: string;
    value: number;
    max: number;
    warn?: boolean;
}

function TrialLimitBar({ label, value, max, warn }: TrialLimitBarProps) {
    const pct = Math.min(100, (value / max) * 100);
    return (
        <div className="trial-limit-bar">
            <div className="trial-limit-bar__header">
                <span className="trial-limit-bar__label">{label}</span>
                <span className={`trial-limit-bar__count${warn ? " trial-limit-bar__count--warn" : ""}`}>{value}/{max}</span>
            </div>
            <div className="trial-limit-bar__track">
                <div
                    className={`trial-limit-bar__fill${warn ? " trial-limit-bar__fill--warn" : ""}`}
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}

interface TrialLimitsProps {
    isTrialing: boolean;
}

export function TrialLimits({ isTrialing }: TrialLimitsProps) {
    const { t } = useTranslation();
    const { data, refetch } = useApi<TrialLimitsData>("/trial/limits", isTrialing ? 1 : 0);

    useEffect(() => {
        if (!isTrialing) return;
        const tick = () => {
            if (document.visibilityState === "visible") refetch();
        };
        // Periodic refresh only while the tab is visible — an unguarded interval here
        // fires forever even when the tab is backgrounded/minimized, which (combined
        // with the backend's sliding session expiry) silently keeps a trial account's
        // session alive indefinitely regardless of actual inactivity.
        const id = setInterval(tick, 30_000);
        // Catch up immediately when the user switches back to this tab
        document.addEventListener("visibilitychange", tick);
        return () => {
            clearInterval(id);
            document.removeEventListener("visibilitychange", tick);
        };
    }, [isTrialing, refetch]);

    if (!isTrialing || !data) return null;

    const invoiceWarn = data.invoiceCount >= data.invoiceLimit * 0.8;
    const smsWarn     = data.smsCount >= data.smsLimit * 0.8;

    const daysLabel = data.daysLeft === 0
        ? t("trial.daysLeft_zero")
        : t(`trial.daysLeft_${data.daysLeft === 1 ? "one" : "other"}`, { count: data.daysLeft });

    return (
        <div className="trial-limits">
            <div className="trial-limits__header">
                <span className="trial-limits__title">{t("trial.limitsTitle")}</span>
                <span className={`trial-limits__days${data.daysLeft <= 1 ? " trial-limits__days--urgent" : ""}`}>
                    {daysLabel}
                </span>
            </div>
            <TrialLimitBar
                label={t("trial.invoices")}
                value={data.invoiceCount}
                max={data.invoiceLimit}
                warn={invoiceWarn}
            />
            <TrialLimitBar
                label={t("trial.sms")}
                value={data.smsCount}
                max={data.smsLimit}
                warn={smsWarn}
            />
        </div>
    );
}
